/* Angular modules */
import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
/* ngx modules */
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {SessionStorage} from 'ngx-webstorage';
/* app modules */
import {Item, Project, Status} from '../../project/shared/project-model';
import {LoggerService} from '../../shared/logging/logger.service';
import {NavigationStep} from '../../shared/guards/navigationStep-enum';
import {ItemService} from '../../project/shared/item.service';
import {UtilService} from 'app/shared/util/util.service';
import {Router} from '@angular/router';
import {ComponentService} from '../../configuration/shared/services/component.service';
import {ProductsFiltersService} from '../../shared/products-filters/products-filters.service';
import {ComponentSearchModeEnum, SwitchBoardComponent} from 'app/configuration/shared/model/component';
import {VisibilityCheck} from 'app/configuration/shared/model/visibilityCheck';
import {FilterCategory} from '../../configuration/shared/model/filterCategory';
import {Opt} from 'app/configuration/shared/model/IProduct';
import {CommonModalComponent} from '../../shared/common-modal/common-modal.component';
import {ProjectService} from '../../project/shared/project.service';
import {DatasheetsService} from '../../shared/datasheets/datasheets.service';
import {NavigationRoute} from '../../shared/guards/route-enum';
import {Subject, forkJoin} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import * as _ from 'lodash';
import {Documentation} from "../../shared/documentation/documentation.model";
import {
  AccessoriesFamily,
  CharacteristicType,
  CharacteristicView
} from "../../characteristics/shared/characteristics.view";
import {Observable} from 'rxjs';
import {AccessoriesService} from "../../shared/services/accessories.service";
import {AccessoryCategoriesService} from "../../shared/services/accessoryCategories.service";
import {CharacteristicsHelperService} from "../../characteristics/shared/characteristics.helper.service";
import {User} from 'app/shared/user/user';
import {option} from "@se/web-ui/types/components/checkbox/constants";

export enum ChangeOperation {
  ChangeTransformerCharacteristics = 1,
  ChangeOption = 2
}

@Component({
  standalone: false,
  selector: 'app-transformer-selector',
  templateUrl: './transformer-selector.component.html',
  styleUrls: ['./transformer-selector.component.less'],
})

export class TransformerSelectorComponent implements OnInit, OnDestroy {
  @SessionStorage()
  currentProject: Project;
  @SessionStorage()
  currentItemct: Item;
  @SessionStorage()
  user: User;
  @ViewChild('LooseModal')
  LooseModal: CommonModalComponent;

  @ViewChild('container') container: ElementRef;

  loading = true;
  isNeededToBeReinitialised = false;
  // Item CT in the DB for the reinitialisation
  databaseItemct: Item;
  allComponents: Array<SwitchBoardComponent>;
  filteredComponents: Array<SwitchBoardComponent>;
  filters: Array<FilterCategory> = [];
  selectedComponent: SwitchBoardComponent;
  settingsEnded = false;
  transformerSelectorStepNumber = NavigationStep.TRANSFORMER_SELECTOR;
  savedValue: any;
  savedKey: string;
  savedOption: Opt;
  modificationOperation: ChangeOperation;
  allAccessories: AccessoriesFamily[] = [];
  accessoriesCharacteristicsValues: CharacteristicView[] = [];
  unsubscribe$: Subject<void> = new Subject<void>();
  mandatoryAccessoryMissing = false;
  mandatoryOptionMissing = false;
  categoryIds: string[];
  categoryGroups: Partial<Record<string, Opt[]>>;

  constructor(private characteristicsService: AccessoriesService,
              private categoryService: AccessoryCategoriesService,
              private componentService: ComponentService,
              private productsFiltersService: ProductsFiltersService,
              private logger: LoggerService,
              private router: Router,
              private itemService: ItemService,
              private datasheetsService: DatasheetsService,
              private projectService: ProjectService,
              private translateService: TranslateService,
              private messageService: MessageService,
              private characteristicsHelperService: CharacteristicsHelperService) {
  }

  ngOnInit() {
    this.logger.debug('TransformerSelectorComponent init()');
    this.loading = true;
    this.itemService.setItemNavigationStep(NavigationStep.TRANSFORMER_SELECTOR, this.user, this.unsubscribe$);
    if (this.currentItemct && this.currentItemct.status === Status.ordered && !this.projectService.isReadOnlyProject(this.currentProject, this.user)) {
      if (this.projectService.isCpqProject(this.currentProject)) {
        // Information Toast
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('T_ORDER_WARNING_TITLE'),
          detail: this.translateService.instant('T_PUSH_TO_CPQ_WARNING')
        });
      } else {
        // Information Toast Ordered
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('T_ORDER_WARNING_TITLE'),
          detail: this.translateService.instant('T_ORDER_WARNING')
        });
      }
    } else {
      if (this.currentItemct && this.currentItemct.status === Status.quoted && !this.projectService.isReadOnlyProject(this.currentProject, this.user)) {
        this.isNeededToBeReinitialised = true;
        this.databaseItemct = UtilService.clone(this.currentItemct);
        // Information Toast Loose data
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('T_LOOSE_DATA_WARNING'),
          detail: this.translateService.instant('T_LOOSE_DATA_WARNING_TITLE')
        });
      }
    }

    this.itemService.updateItem(this.currentItemct, this.user)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(item => {
        this.currentItemct = item;
        this.loadConfigurationData();
      });
  }

  loadConfigurationData() {
    forkJoin([
      this.characteristicsService.getPublishedAccessoriesCharacteristics(this.currentItemct.range.id, true),
      this.componentService.getComponents(this.currentItemct.id, ComponentSearchModeEnum.MAIN),
      this.categoryService.getAccessoryCategory(this.currentItemct.range.id, true)
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(results => {
        const accessoriesCharacteristics = results[0];
        this.allComponents = results[1];

        // Filter accessories relevant to current item
        this.accessoriesCharacteristicsValues = accessoriesCharacteristics
          .filter(carac => this.currentItemct.displayAccessories.includes(carac.id))
          .map(carac =>
            this.characteristicsHelperService.convertCharacteristicToView(
              carac,
              CharacteristicType.ACCESSORY,
              this.currentItemct.accessories.filter(ev => carac.id === ev.id)
            )
          );
        if (
          this.accessoriesCharacteristicsValues?.length > 0 &&
          this.currentItemct.status === Status.configured
        ) {
          this.accessoriesCharacteristicsValues.forEach(charac => {
            this.characteristicsHelperService.characteristicViewToItemValue(this.currentItemct, charac);
          });
        }
        this.allAccessories = this.characteristicsHelperService.sortAccessoriesByCategory(this.allAccessories, results[2], this.accessoriesCharacteristicsValues);

        this.filters = this.productsFiltersService.extractFilterSelector(
          this.allComponents,
          this.currentItemct.range.visibleComponentsFilters,
          new Map<string, VisibilityCheck>()
        );

        this.filters.forEach(filter => {
          if (filter.values.length === 1) {
            filter.values[0].checked = true;
          }
        });
        if (this.currentItemct.components.length > 0) {
          this.productsFiltersService.initFilters(this.filters, this.currentItemct.components);
          this.settingsEnded = true;
          this.selectedComponent = this.currentItemct.components[0];
          this.createOptionsGroup();
        }
        this.filteredComponents = <SwitchBoardComponent[]>this.productsFiltersService.applyFilters(
          this.filters,
          this.allComponents
        );

        this.loading = false;
        this.validateMandatoryAccessories();
        this.validateMandatoryOptions();
      });
  }

  /**
   * Unsubscribe observables
   */
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    // unsubscribe from the subject itself:
    this.unsubscribe$.unsubscribe();
  }

  disableValidateButton(): boolean {
    return this.currentItemct.maxNavigationStep <= (this.transformerSelectorStepNumber) || this.isReadOnlyProject() || this.mandatoryAccessoryMissing || this.mandatoryOptionMissing;
  }

  changeTab(): void {
    this.router.navigate([NavigationRoute.getNextStepByRange(this.currentItemct.range, this.currentItemct.currentNavigationStep).link]);
  }

  /**
   * Just for the onChange from select element
   * Call the modal if we need to be reinitialised
   * @param key
   * @param value
   */
  onChangeCharacteristics(key: string, value: string) {
    if (this.isNeededToBeReinitialised) {
      this.savedValue = value;
      this.savedKey = key;
      this.modificationOperation = ChangeOperation.ChangeTransformerCharacteristics;
      this.LooseModal.show();
    } else {
      this.changeCharacteristics(key, value);
    }
  }

  /**
   * Just for the onChange from select element
   * Call the modal if we need to be reinitialised
   * @param value
   * @param optionSelect
   */
  onChangeOption(value: string, optionSelect: Opt) {
    this.changeOption(value, optionSelect);
    this.validateMandatoryOptions();
  }


  confirmLooseData() {
    this.currentItemct.maxNavigationStep = NavigationRoute.getNextStepByRange(this.currentItemct.range, this.currentItemct.currentNavigationStep).id;
    this.currentItemct.status = Status.configured;
    this.logger.logStatus(this.currentItemct);
    this.isNeededToBeReinitialised = false;
    switch (this.modificationOperation) {
      case (ChangeOperation.ChangeOption):
        this.changeOption(this.savedValue, this.savedOption);
        break;
      case (ChangeOperation.ChangeTransformerCharacteristics):
        this.changeCharacteristics(this.savedKey, this.savedValue);
        break;

    }
  }

  /**
   * Method called on the loosing data modal if a user clicks on "NO"
   * Use to re-initiate form data
   */

  reinitData() {
    this.selectedComponent = UtilService.clone(this.databaseItemct.components[0]);
    this.createOptionsGroup();
  }

  hasOptions(): boolean {
    return this.selectedComponent && this.selectedComponent.options.length > 0 &&
      this.selectedComponent.options
        .some(option =>
          option.values
            .some(value => value.reference !== null && value.reference !== undefined && value.reference.ref !== null && value.reference.ref !== undefined)); // At least one option has at least one value (option).
  }

  hasSelectedComponent(): boolean {
    return this.selectedComponent !== null && this.selectedComponent !== undefined;
  }

  getSelectedReference(): string {
    if (this.hasSelectedComponent()) {
      return this.translateService.instant('T_TRANSFORMER_SELECTED_REFERENCE', {reference: this.selectedComponent.reference.ref});
    }
    return '';
  }

  isReadOnlyProject(): boolean {
    return this.projectService.isReadOnlyProject(this.currentProject, this.user) || this.currentItemct.status === Status.ordered;
  }

  noOptionsSelected(option) {
    return (option.values.findIndex(opt => opt.selected === true) === -1 || option.defaultValue === undefined);
  }

  /**
   * Update status of filters as checked or disabled
   * @param key
   * @param value
   * @private
   */
  private changeCharacteristics(key: string, value: string) {
    this.filters.forEach(filterCategory => {
        if (filterCategory.key === key) {
          filterCategory.values.forEach(filterValue => {
            filterValue.checked = filterValue.value.toString() === value;
          });
        }
      }
    );
    this.filteredComponents = <SwitchBoardComponent[]>this.productsFiltersService.applyFilters(this.filters, this.allComponents);
    this.filteredComponents.forEach(component => {
      component.options.forEach(option => {
        const hasValidReference = option.values.some(optionValue => optionValue.reference != null);
        if (hasValidReference && option.values.length > 0) {
          option.values[0].selected = !option.mandatory;
        }
      });
    });
    this.productsFiltersService.updateFilters(this.filters, this.filteredComponents);
    this.settingsEnded = this.filteredComponents.length === 1;
    this.updateTransformer();
  }

  private changeOption(value: string, optionSelect: Opt) {
    this.selectedComponent.options.forEach(option => {
      if (option.name === optionSelect.name && option.category === optionSelect.category) {
        option.values.forEach(optionValue => {
          optionValue.selected = optionValue.value.toString() === value;
        });
      }
    });
    this.updateMaxNextStep();
    this.updateItem();
  }

  /**
   * used to add a transformer (an item as transformer)
   */
  private updateTransformer() {
    if (this.settingsEnded) {
      this.selectedComponent = this.filteredComponents[0];

      if (this.currentItemct.components[0] !== this.selectedComponent) {
        this.createOptionsGroup();
        this.loading = true;
        const components: [SwitchBoardComponent] = [this.selectedComponent];
        this.datasheetsService
          .getComponentsDatasheetUrl(components, this.user.preferredLanguage)
          .subscribe(payloadDocumentList => {
            this.addTransformerWithDocuments(payloadDocumentList, components);
          });
      }
    } else if (this.currentItemct.components.length > 0) {
      this.currentItemct.components.splice(0, this.currentItemct.components.length);
      this.removeTransformer();
    }
    this.selectedComponent = null;
  }

  private addTransformerWithDocuments(payloadDocumentList: Array<{
    [k: string]: Documentation
  }>, components: [SwitchBoardComponent]) {
    if (payloadDocumentList.length === 0) {
      this.selectedComponent = components[0];
      this.currentItemct.components = [];
      this.currentItemct.components.push(this.selectedComponent);
      this.addTransformer();
      return;
    }

    payloadDocumentList.forEach((payload: { [p: string]: Documentation }) => {
      const payloadComponentId = _.keys(payload)[0];
      const findComponent = components.find(c => c.id === payloadComponentId);
      if (findComponent !== undefined) {
        findComponent.documents = _.uniqBy(_.concat(findComponent.documents, payload[payloadComponentId]), 'id');
      }
      this.selectedComponent = findComponent;
      this.createOptionsGroup();
    });

    this.currentItemct.components[0] = this.selectedComponent;
    this.addTransformer();
  }

  private addTransformer() {
    this.updateMaxNextStep();
    this.itemService.updateItemWithStatusChange(this.currentItemct, Status.configured, this.user)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(item => {
          this.currentItemct = item;
          this.selectedComponent = item.components[0];
          this.createOptionsGroup();
          this.itemService.updateTotalPriceAndDimensions(this.unsubscribe$);
          this.validateMandatoryOptions();
        },
        () => this.loading = false,
        () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('T_INFORMATION'),
            detail: this.translateService.instant(this.currentItemct.components[0].name) + ' ' + this.translateService.instant('T_SUCCESS_ITEM_ADDED'),
          });
          this.loading = false;
        });

  }


  private updateMaxNextStep() {
    this.validateMandatoryOptions();
    this.validateMandatoryAccessories();
    if (!this.mandatoryOptionMissing && !this.mandatoryAccessoryMissing && this.selectedComponent !== null && this.selectedComponent !== undefined) {
      this.currentItemct.maxNavigationStep = NavigationRoute.getNextStepByRange(this.currentItemct.range, this.currentItemct.currentNavigationStep).id;
    } else {
      this.currentItemct.maxNavigationStep = NavigationStep.TRANSFORMER_SELECTOR;
    }
  }

  private removeTransformer() {
    this.itemService.updateItemWithStatusChange(this.currentItemct, Status.configured, this.user)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(item => {
          this.currentItemct = item;
          this.itemService.updateTotalPriceAndDimensions(this.unsubscribe$);
          this.currentItemct.maxNavigationStep = NavigationStep.TRANSFORMER_SELECTOR;
        },
        error => null,
        () => this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('T_INFORMATION'),
          detail: this.translateService.instant('T_SUCCESS_REMOVE_ITEM')
        }));
  }

  openAccessoryCategoryChanged(accessoriesFamilyIndex: number, isOpen: boolean) {
    this.allAccessories[accessoriesFamilyIndex].display = isOpen;
  }

  /**
   * Apply user input to configuration
   * @param {CharacteristicView} element the characteristics changed
   */
  changeAccessoryValue(element: CharacteristicView) {
    if (element.options.every(opt => !opt.selected)) {
      this.currentItemct.accessories = this.currentItemct.accessories.filter(acc => acc.id !== element.id);
    } else {
      // Convert all selected options to ItemValue
      this.characteristicsHelperService.characteristicViewToItemValue(this.currentItemct, element);
    }
    this.updateMaxNextStep();
    this.updateItem();
    this.loadConfigurationData()
  }

  scrollDown() {
    this.container.nativeElement.scrollIntoView(false);
  }

  private updateItem() {
    this.itemService.updateItemWithStatusChange(this.currentItemct, Status.configured, this.user)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(item => {
        this.selectedComponent = item.components[0];
        this.createOptionsGroup();
        this.currentItemct = item;
        this.itemService.updateTotalPriceAndDimensions(this.unsubscribe$);
      });
  }

  /**
   * Check if any mandatory accessory is missing
   */
  validateMandatoryAccessories(): void {
    if (!this.accessoriesCharacteristicsValues?.length) {
      this.mandatoryAccessoryMissing = false;
      return;
    }
    this.mandatoryAccessoryMissing =
      this.accessoriesCharacteristicsValues
        .filter(characteristic => characteristic.required)
        .some(characteristic =>
          !characteristic.options ||
          characteristic.options.every(opt => !opt.selected)
        );
  }

  validateMandatoryOptions(): void {
    if (!this.hasOptions()) {
      this.mandatoryOptionMissing = false;
      return;
    }
    this.mandatoryOptionMissing =
      this.selectedComponent?.options
        .filter(option => this.isOptionVisible(option))
        .some(option =>
        option.mandatory &&
        option.values
          .every(value => !value.selected));
  }

  private createOptionsGroup() {
    const cubicleOptions = this.selectedComponent?.options
      ?.filter(option => this.isOptionVisible(option));
    this.categoryGroups = Object.groupBy(cubicleOptions, ({category}) => category?.id);
    this.categoryIds = ['undefined', ...new Set(cubicleOptions
      .filter(option => option.category !== null && option.category !== undefined)
      .map(option => option.category?.id))];

  }
  private isOptionVisible(option: Opt): boolean {
    const valueCount = option.values?.length || 0;
    if (option.mandatory && valueCount === 1) {
      return false;
    }
    return true;
  }
}
